// src/components/proposal/DeliverableCard.tsx
// Card component for displaying individual deliverable with labor breakdown

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Users,
  Sparkles,
  FileText,
  Package,
  Code,
  GraduationCap,
  Briefcase,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import type { DeliverableWithLabor } from '../../types/deliverables';

interface DeliverableCardProps {
  deliverable: DeliverableWithLabor;
  index: number;
  onEnhance?: () => void;
  isEnhancing?: boolean;
}

const getDeliverableIcon = (type?: string) => {
  switch (type?.toLowerCase()) {
    case 'document':
    case 'report':
      return FileText;
    case 'product':
      return Package;
    case 'software':
      return Code;
    case 'training':
      return GraduationCap;
    case 'service':
      return Briefcase;
    default:
      return Package;
  }
};

const getTypeColor = (type?: string) => {
  switch (type?.toLowerCase()) {
    case 'document':
      return 'bg-bw-primary-tint text-bw-primary';
    case 'report':
      return 'bg-bw-primary-tint text-bw-primary';
    case 'product':
      return 'bg-success-bg text-success';
    case 'software':
      return 'bg-bw-primary-tint text-bw-primary';
    case 'training':
      return 'bg-fit-moderate-bg text-fit-moderate';
    case 'service':
      return 'bg-teal-100 text-teal-700';
    default:
      return 'bg-bw-surface-subtle text-bw-ink';
  }
};

export function DeliverableCard({
  deliverable,
  index,
  onEnhance,
  isEnhancing = false,
}: DeliverableCardProps) {
  const [expanded, setExpanded] = useState(false);
  const Icon = getDeliverableIcon(deliverable.type);
  const typeColor = getTypeColor(deliverable.type);

  return (
    <div className="border rounded-lg bg-bw-surface shadow-sm hover:shadow-card transition-shadow">
      {/* Header */}
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-sm font-mono font-medium">
                {deliverable.deliverable_id}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${typeColor}`}>
                {deliverable.type || 'Other'}
              </span>
              {deliverable.quantity && (
                <span className="text-xs text-bw-muted">
                  Qty: {deliverable.quantity} {deliverable.unit || ''}
                </span>
              )}
            </div>
            <h4 className="font-semibold text-lg text-bw-ink mt-2 flex items-center gap-2">
              <Icon className="h-5 w-5 text-bw-muted" />
              {deliverable.deliverable_name}
            </h4>
            {deliverable.description && (
              <p className="text-bw-muted text-sm mt-1 line-clamp-2">
                {deliverable.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 ml-4">
            {/* Hours Badge */}
            <div className="text-right">
              <div className="flex items-center gap-1 text-bw-primary">
                <Clock className="h-4 w-4" />
                <span className="font-semibold">
                  {deliverable.total_hours?.recommended ?? 0}
                </span>
                <span className="text-sm">hrs</span>
              </div>
              <span className="text-xs text-bw-muted">
                ({deliverable.total_hours?.min ?? 0}-{deliverable.total_hours?.max ?? 0})
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {onEnhance && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEnhance();
                  }}
                  disabled={isEnhancing}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-bw-primary hover:text-bw-primary hover:bg-bw-primary-tint rounded transition-colors disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  {isEnhancing ? 'Enhancing...' : 'Enhance'}
                </button>
              )}
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-1 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded transition-colors"
              >
                {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-bw-border space-y-4">
          {/* Specifications */}
          {deliverable.specifications && (
            <div>
              <h5 className="font-medium text-sm text-bw-ink mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Specifications
              </h5>
              <div className="bg-bw-surface-subtle rounded-lg p-3 space-y-2">
                {deliverable.specifications.format && (
                  <div className="text-sm">
                    <span className="font-medium text-bw-muted">Format:</span>{' '}
                    <span className="text-bw-ink">{deliverable.specifications.format}</span>
                  </div>
                )}
                {deliverable.specifications.technical_requirements && deliverable.specifications.technical_requirements.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-bw-muted">Technical Requirements:</span>
                    <ul className="list-disc list-inside text-sm text-bw-ink mt-1 space-y-0.5">
                      {deliverable.specifications.technical_requirements.map((req, i) => (
                        <li key={i}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {deliverable.specifications.acceptance_criteria && deliverable.specifications.acceptance_criteria.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-bw-muted">Acceptance Criteria:</span>
                    <ul className="list-disc list-inside text-sm text-bw-ink mt-1 space-y-0.5">
                      {deliverable.specifications.acceptance_criteria.map((criterion, i) => (
                        <li key={i}>{criterion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          {deliverable.timeline && (
            <div>
              <h5 className="font-medium text-sm text-bw-ink mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Timeline
              </h5>
              <div className="bg-bw-surface-subtle rounded-lg p-3 text-sm">
                {deliverable.timeline.due_date && (
                  <div>
                    <span className="font-medium text-bw-muted">Due:</span>{' '}
                    <span className="text-bw-ink">{deliverable.timeline.due_date}</span>
                  </div>
                )}
                {deliverable.timeline.duration && (
                  <div>
                    <span className="font-medium text-bw-muted">Duration:</span>{' '}
                    <span className="text-bw-ink">{deliverable.timeline.duration}</span>
                  </div>
                )}
                {deliverable.timeline.dependencies && deliverable.timeline.dependencies.length > 0 && (
                  <div>
                    <span className="font-medium text-bw-muted">Dependencies:</span>{' '}
                    <span className="text-bw-ink">{deliverable.timeline.dependencies.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Labor Breakdown */}
          {deliverable.labor_phases && deliverable.labor_phases.length > 0 && (
            <div>
              <h5 className="font-medium text-sm text-bw-ink mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Labor Breakdown
              </h5>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-bw-surface-subtle">
                    <tr>
                      <th className="text-left p-2 font-medium text-bw-muted">Phase</th>
                      <th className="text-right p-2 font-medium text-bw-muted">Hours</th>
                      <th className="text-left p-2 font-medium text-bw-muted">Labor Categories</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliverable.labor_phases.map((phase, i) => (
                      <tr key={i} className="border-b border-bw-border last:border-0">
                        <td className="p-2">
                          <div className="font-medium text-bw-ink">{phase.phase_name}</div>
                          {phase.description && (
                            <div className="text-xs text-bw-muted">{phase.description}</div>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <span className="font-semibold text-bw-primary">
                            {phase.hours_estimate?.recommended ?? 0}
                          </span>
                          <span className="text-xs text-bw-muted ml-1">
                            ({phase.hours_estimate?.min ?? 0}-{phase.hours_estimate?.max ?? 0})
                          </span>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {phase.labor_categories.map((cat, j) => (
                              <span
                                key={j}
                                className="inline-flex items-center px-2 py-0.5 bg-bw-primary-tint text-bw-primary rounded text-xs"
                                title={cat.sca_title ? `SCA: ${cat.sca_title}` : undefined}
                              >
                                {cat.category} ({cat.hours_percent}%)
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-bw-surface-subtle">
                    <tr>
                      <td className="p-2 font-semibold text-bw-ink">Total</td>
                      <td className="p-2 text-right font-bold text-bw-primary">
                        {deliverable.total_hours?.recommended ?? 0} hrs
                      </td>
                      <td className="p-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Compliance Requirements */}
          {deliverable.compliance_requirements && deliverable.compliance_requirements.length > 0 && (
            <div>
              <h5 className="font-medium text-sm text-bw-ink mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Compliance Requirements
              </h5>
              <div className="flex flex-wrap gap-2">
                {deliverable.compliance_requirements.map((req, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-1 bg-fit-moderate-bg text-fit-moderate border border-yellow-200 rounded text-xs"
                    title={`${req.description}\n\nApplicability: ${req.applicability}\n\nVerification: ${req.verification_method}`}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {req.requirement_id}: {req.requirement_name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Compliance Deliverables */}
          {deliverable.compliance_deliverables && deliverable.compliance_deliverables.length > 0 && (
            <div>
              <h5 className="font-medium text-sm text-bw-ink mb-2">Compliance Deliverables</h5>
              <div className="space-y-2">
                {deliverable.compliance_deliverables.map((cd, i) => (
                  <div key={i} className="bg-fit-moderate-bg border border-fit-moderate-border rounded p-2 text-sm">
                    <div className="font-medium text-fit-moderate">{cd.name}</div>
                    <div className="text-fit-moderate text-xs">{cd.description}</div>
                    <div className="text-fit-moderate text-xs mt-1">
                      Format: {cd.format} | Timing: {cd.timing}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Source Reference */}
          {deliverable.source_reference && (
            <div className="text-xs text-bw-muted pt-2 border-t border-bw-border">
              Source: {deliverable.source_reference}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DeliverableCard;
